import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, ShieldCheck, Tag, X, CreditCard, Smartphone, Building2 } from "lucide-react";
import { api, inr, formatApiErrorDetail, loadRazorpay } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useCustomer } from "../context/CustomerContext";
import { toast } from "sonner";

const EMPTY_FORM = { name: "", email: "", phone: "", line1: "", city: "", state: "", pincode: "" };

const Checkout = () => {
  const { items, subtotal, setQty, clear } = useCart();
  const { user: customer } = useCustomer();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [demoOrder, setDemoOrder] = useState(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [payMode, setPayMode] = useState("");

  useEffect(() => {
    if (customer) setForm((f) => ({ ...f, name: f.name || customer.name, email: f.email || customer.email }));
  }, [customer]);

  useEffect(() => {
    if (items.length === 0) return;
    api.post("/quote", {
      items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
      promo_code: appliedPromo,
    }).then((r) => setQuote(r.data)).catch(() => {});
  }, [items, appliedPromo]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) er.email = "Enter a valid email";
    if (!/^[6-9]\d{9}$/.test(form.phone.trim())) er.phone = "Enter a valid 10-digit mobile";
    if (!form.line1.trim()) er.line1 = "Required";
    if (!form.city.trim()) er.city = "Required";
    if (!form.state.trim()) er.state = "Required";
    if (!/^\d{6}$/.test(form.pincode.trim())) er.pincode = "6-digit PIN";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const applyPromo = () => {
    if (!promo.trim()) return;
    setAppliedPromo(promo.trim().toUpperCase());
    toast.success(`Code ${promo.trim().toUpperCase()} applied`);
  };

  const pay = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setPlacing(true);
    try {
      const { data: order } = await api.post("/orders", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        customer: { name: form.name, email: form.email, phone: form.phone },
        address: { line1: form.line1, city: form.city, state: form.state, pincode: form.pincode },
        promo_code: appliedPromo,
      });
      const { data: ck } = await api.post("/checkout", { order_number: order.order_number });
      if (ck.mode === "razorpay") {
        const ok = await loadRazorpay();
        if (!ok) throw new Error("Could not load payment gateway. Check your connection.");
        const rzp = new window.Razorpay({
          key: ck.key_id,
          amount: ck.amount,
          currency: "INR",
          order_id: ck.razorpay_order_id,
          name: "Jwellary",
          description: `Order ${ck.order_number}`,
          image: `${window.location.origin}/logo-full.png`,
          theme: { color: "#D4AF37" },
          prefill: { name: form.name, email: form.email, contact: form.phone },
          handler: async (res) => {
            try {
              await api.post("/verify-payment", {
                order_number: ck.order_number,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_signature: res.razorpay_signature,
              });
              clear();
              navigate(`/order-confirmation?order=${ck.order_number}`);
            } catch {
              toast.error("Payment verification failed. Please contact support.");
            }
          },
        });
        rzp.on("payment.failed", () => toast.error("Payment failed. Your order is saved as pending."));
        rzp.open();
      } else {
        setDemoOrder(ck);
        setPayMode("demo");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPlacing(false);
    }
  };

  const confirmDemo = async () => {
    setDemoBusy(true);
    try {
      await api.post("/checkout/demo", { order_number: demoOrder.order_number });
      clear();
      navigate(`/order-confirmation?order=${demoOrder.order_number}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDemoBusy(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="pt-[68px] min-h-screen flex items-center justify-center px-6" data-testid="checkout-empty">
        <div className="text-center max-w-sm">
          <p className="font-serif text-4xl text-zinc-200">Your bag is empty</p>
          <p className="text-sm text-zinc-500 mt-3">Add a piece you love, then return to complete your order.</p>
          <Link to="/shop" className="btn-gold mt-8">Shop the Collection</Link>
        </div>
      </main>
    );
  }

  const totals = quote || { subtotal, discount: 0, shipping: subtotal < 499 ? 50 : 99, total: subtotal + (subtotal < 499 ? 50 : 99) };
  const field = (k, label, placeholder, extra = {}) => (
    <div>
      <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">{label}</label>
      <input
        value={form[k]}
        onChange={set(k)}
        placeholder={placeholder}
        className={`input-dark ${errors[k] ? "border-red-500/60" : ""}`}
        data-testid={`checkout-${k}-input`}
        {...extra}
      />
      {errors[k] && <p className="text-xs text-red-400 mt-1.5" data-testid={`checkout-${k}-error`}>{errors[k]}</p>}
    </div>
  );

  return (
    <main className="pt-[68px] min-h-screen" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <p className="eyebrow mb-3">Almost Yours</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl text-zinc-100 mb-12">Checkout</h1>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14">
          <div className="lg:col-span-3 space-y-10">
            <section data-testid="checkout-contact-section">
              <h2 className="font-serif text-2xl text-zinc-100 mb-6">Contact</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {field("name", "Full Name", "Aishwarya Rai")}
                {field("email", "Email", "you@example.com", { type: "email" })}
                {field("phone", "Mobile", "98765 43210", { inputMode: "numeric", maxLength: 10 })}
              </div>
            </section>

            <section data-testid="checkout-address-section">
              <h2 className="font-serif text-2xl text-zinc-100 mb-6">Delivery Address</h2>
              <div className="grid gap-5">
                {field("line1", "Address", "Flat, street, landmark")}
                <div className="grid sm:grid-cols-3 gap-5">
                  {field("city", "City", "Mumbai")}
                  {field("state", "State", "Maharashtra")}
                  {field("pincode", "PIN Code", "400001", { inputMode: "numeric", maxLength: 6 })}
                </div>
              </div>
            </section>

            <div className="flex items-center gap-2 text-xs text-zinc-500" data-testid="checkout-secure-note">
              <Lock size={13} className="text-[#d4af37]" />
              Your details are used only to fulfil this order.
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-[#d4af37]/25 bg-[#101013] p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
              <h2 className="font-serif text-2xl text-zinc-100 mb-6">Order Summary</h2>
              <div className="space-y-4 max-h-64 overflow-y-auto pr-1">
                {items.map((i) => (
                  <div key={i.product_id} className="flex gap-3 items-center" data-testid={`summary-item-${i.product_id}`}>
                    <img src={i.image} alt={i.name} className="w-14 h-16 object-cover rounded-md border border-[#d4af37]/15" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-zinc-200 truncate">{i.name}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <button onClick={() => setQty(i.product_id, i.qty - 1)} className="text-zinc-500 hover:text-[#f3e5ab] text-xs" data-testid={`summary-decrement-${i.product_id}`}>−</button>
                        <span className="text-xs text-zinc-400" data-testid={`summary-qty-${i.product_id}`}>Qty {i.qty}</span>
                        <button onClick={() => setQty(i.product_id, i.qty + 1)} className="text-zinc-500 hover:text-[#f3e5ab] text-xs" data-testid={`summary-increment-${i.product_id}`}>+</button>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-300">{inr(i.price * i.qty)}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex gap-2" data-testid="checkout-promo">
                <input
                  value={promo}
                  onChange={(e) => setPromo(e.target.value)}
                  placeholder="Promo code — try AURELIA10"
                  className="input-dark flex-1 py-2.5"
                  data-testid="checkout-promo-input"
                />
                <button onClick={applyPromo} className="btn-ghost !px-5 !py-2.5" data-testid="checkout-promo-apply-button">
                  <Tag size={14} /> Apply
                </button>
              </div>

              <div className="mt-6 pt-5 border-t border-[#d4af37]/15 space-y-2.5 text-sm" data-testid="checkout-totals">
                <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{inr(totals.subtotal)}</span></div>
                {totals.discount > 0 && (
                  <div className="flex justify-between text-emerald-400" data-testid="checkout-discount"><span>Discount (AURELIA10)</span><span>−{inr(totals.discount)}</span></div>
                )}
                <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>{totals.shipping === 0 ? "Free" : inr(totals.shipping)}</span></div>
                <div className="flex justify-between text-zinc-100 font-semibold text-base pt-2 border-t border-[#d4af37]/15" data-testid="checkout-total">
                  <span>Total</span><span>{inr(totals.total)}</span>
                </div>
              </div>

              <button onClick={pay} disabled={placing} className="btn-gold w-full mt-6" data-testid="checkout-pay-button">
                <Lock size={15} /> {placing ? "Placing order..." : `Pay ${inr(totals.total)} Securely`}
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-zinc-600">
                <ShieldCheck size={13} /> {payMode === "razorpay" ? "Payments by Razorpay · UPI, Cards, NetBanking" : "Demo mode — no real charge"}
              </div>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {demoOrder && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center px-5"
            data-testid="demo-payment-modal"
          >
            <motion.div
              initial={{ scale: 0.92, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-md rounded-2xl border border-[#d4af37]/30 bg-[#101013] p-8 relative"
              data-testid="demo-payment-card"
            >
              <button
                onClick={() => setDemoOrder(null)}
                className="absolute top-4 right-4 p-2 rounded-full text-zinc-500 hover:text-zinc-200"
                data-testid="demo-payment-close-button"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <p className="eyebrow mb-2">Demo Checkout</p>
              <h3 className="font-serif text-3xl text-zinc-100">{inr(demoOrder.amount / 100)}</h3>
              <p className="text-sm text-zinc-500 mt-2 leading-relaxed">
                Razorpay keys aren't configured yet, so this is a simulated payment — nothing is
                charged. Add RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET to switch to live payments
                automatically.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-2">
                {[
                  { icon: Smartphone, label: "UPI" },
                  { icon: CreditCard, label: "Card" },
                  { icon: Building2, label: "NetBanking" },
                ].map((m) => (
                  <div key={m.label} className="rounded-lg border border-[#d4af37]/20 bg-[#0c0c0e] py-4 flex flex-col items-center gap-1.5 text-zinc-400">
                    <m.icon size={17} className="text-[#d4af37]" />
                    <span className="text-[11px] tracking-wide">{m.label}</span>
                  </div>
                ))}
              </div>
              <button onClick={confirmDemo} disabled={demoBusy} className="btn-gold w-full mt-7" data-testid="demo-payment-confirm-button">
                {demoBusy ? "Processing..." : "Simulate Successful Payment"}
              </button>
              <button
                onClick={() => { setDemoOrder(null); toast.info("Order saved as pending"); }}
                className="w-full mt-3 text-xs tracking-wide uppercase text-zinc-500 hover:text-zinc-300 py-2"
                data-testid="demo-payment-cancel-button"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default Checkout;
